import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Plus, X, GripVertical } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { useEditMode } from '@/contexts/EditModeContext';
import { useAuth } from '@/contexts/AuthContext';

interface FeatureHighlight {
  icon: string;
  text: string;
  type?: 'emoji' | 'lucide';
}

interface EditableFeatureHighlightsProps {
  value: FeatureHighlight[];
  onSave: (value: FeatureHighlight[]) => Promise<void>;
}

export const EditableFeatureHighlights = ({ value, onSave }: EditableFeatureHighlightsProps) => {
  const [items, setItems] = useState<FeatureHighlight[]>(value || []);
  const [isSaving, setIsSaving] = useState(false);
  const { isEditMode } = useEditMode();
  const { isEditor } = useAuth();

  if (!isEditor || !isEditMode) {
    return null;
  }

  const handleAddItem = () => {
    setItems([...items, { icon: '✓', text: 'New feature', type: 'emoji' }]);
  };

  const handleRemoveItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleUpdateItem = (index: number, field: 'icon' | 'text' | 'type', newValue: string) => {
    const newItems = [...items];
    if (field === 'type') {
      newItems[index][field] = newValue as 'emoji' | 'lucide';
    } else {
      newItems[index][field] = newValue;
    }
    setItems(newItems);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(items);
    } finally {
      setIsSaving(false);
    }
  };

  const renderIcon = (icon: string, type?: 'emoji' | 'lucide') => {
    if (type === 'lucide') {
      const IconComponent = (LucideIcons as any)[icon];
      if (IconComponent) {
        return <IconComponent className="w-5 h-5" />;
      }
    }
    return <span className="text-xl">{icon}</span>;
  };

  return (
    <Card className="p-4 space-y-4 bg-muted/50">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold">Edit Feature Highlights</h4>
        <Button onClick={handleSave} disabled={isSaving} size="sm">
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <div className="space-y-3">
        {items.map((item, index) => (
          <div key={index} className="flex items-start gap-2 p-3 bg-background rounded-lg border">
            <GripVertical className="w-4 h-4 mt-2 text-muted-foreground cursor-grab" />
            
            <div className="flex-1 space-y-2">
              <div className="flex gap-2">
                <div className="w-20">
                  <select
                    value={item.type || 'emoji'}
                    onChange={(e) => handleUpdateItem(index, 'type', e.target.value)}
                    className="w-full px-2 py-1 text-xs border rounded"
                  >
                    <option value="emoji">Emoji</option>
                    <option value="lucide">Icon</option>
                  </select>
                </div>
                <Input
                  value={item.icon}
                  onChange={(e) => handleUpdateItem(index, 'icon', e.target.value)}
                  placeholder={item.type === 'lucide' ? 'Icon name' : 'Emoji'}
                  className="w-32"
                />
                <div className="flex items-center justify-center w-10 h-10 border rounded">
                  {renderIcon(item.icon, item.type)}
                </div>
              </div>
              
              <Input
                value={item.text}
                onChange={(e) => handleUpdateItem(index, 'text', e.target.value)}
                placeholder="Feature text"
                className="flex-1"
              />
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleRemoveItem(index)}
              className="text-destructive hover:text-destructive"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        ))}
      </div>

      <Button onClick={handleAddItem} variant="outline" size="sm" className="w-full">
        <Plus className="w-4 h-4 mr-2" />
        Add Feature Highlight
      </Button>
    </Card>
  );
};